import React, { useEffect, useState } from 'react';
import { 
  TrendingUp, TrendingDown, Users, Package, ShoppingCart, DollarSign,
  Store, Clock, ArrowUpRight, UserPlus, CreditCard, LogIn, Trash2,
  Pencil, FolderOpen, Star, Settings, Shield, RefreshCw, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { admin } from '@/lib/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const MetricCard = ({ title, value, change, changeType, icon: Icon, color, subtitle }) => {
  const isPositive = changeType === 'positive';
  const isNegative = changeType === 'negative';
  const colorClasses = {
    red: 'from-red-500 to-red-600',
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    amber: 'from-amber-500 to-amber-600',
    slate: 'from-slate-500 to-slate-600'
  };

  return (
    <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 border-slate-200/50">
      <div className={cn("absolute inset-0 opacity-5 bg-gradient-to-br", colorClasses[color] || colorClasses.slate)} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
          <div className={cn("p-3 rounded-xl bg-gradient-to-br shadow-lg", colorClasses[color] || colorClasses.slate)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {change !== undefined && change !== null && (
          <div className="mt-4 flex items-center gap-2">
            <span className={cn(
              "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold",
              isPositive && "bg-emerald-100 text-emerald-700",
              isNegative && "bg-red-100 text-red-700",
              !isPositive && !isNegative && "bg-slate-100 text-slate-700"
            )}>
              {isPositive ? <TrendingUp className="w-3 h-3" /> : isNegative ? <TrendingDown className="w-3 h-3" /> : null}
              {change}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const activityIconMap = {
  'Giris yapti': { icon: LogIn, color: 'blue' },
  'Kayit oldu': { icon: UserPlus, color: 'green' },
  'Siparis': { icon: ShoppingCart, color: 'green' },
  'siparis': { icon: ShoppingCart, color: 'green' },
  'Urun': { icon: Package, color: 'purple' },
  'urun': { icon: Package, color: 'purple' },
  'Kategori': { icon: FolderOpen, color: 'amber' },
  'kategori': { icon: FolderOpen, color: 'amber' },
  'Kullanici': { icon: Users, color: 'blue' },
  'kullanici': { icon: Users, color: 'blue' },
  'sildi': { icon: Trash2, color: 'red' },
  'guncelledi': { icon: Pencil, color: 'amber' },
  'olusturdu': { icon: Package, color: 'green' },
  'Satici': { icon: Store, color: 'amber' },
  'satici': { icon: Store, color: 'amber' },
  'onayladi': { icon: Shield, color: 'green' },
  'reddetti': { icon: Shield, color: 'red' },
  'Iade': { icon: CreditCard, color: 'red' },
  'Odeme': { icon: CreditCard, color: 'green' },
  'Sifre': { icon: Settings, color: 'blue' },
  'Magaza': { icon: Store, color: 'amber' },
  'degerlendirdi': { icon: Star, color: 'amber' },
  'Iletisim': { icon: Settings, color: 'slate' },
  'Statik': { icon: Settings, color: 'slate' },
  'Homepage': { icon: Settings, color: 'slate' },
};

const getActivityVisual = (action) => {
  for (const [key, val] of Object.entries(activityIconMap)) {
    if (action?.includes(key)) return val;
  }
  return { icon: Clock, color: 'slate' };
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Az once';
  if (mins < 60) return `${mins} dk once`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saat once`;
  const days = Math.floor(hours / 24);
  return `${days} gun once`;
};

const RecentActivityItem = ({ activity }) => {
  const visual = getActivityVisual(activity.action);
  const Icon = visual.icon;
  const colorMap = {
    green: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    slate: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid="activity-item">
      <div className={cn("p-2 rounded-lg", colorMap[visual.color] || colorMap.slate)}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{activity.action}</p>
        <p className="text-xs text-slate-500 truncate">
          {activity.user_email || 'Sistem'}
          {activity.detail ? ` - ${activity.detail}` : ''}
        </p>
      </div>
      <span className="text-xs text-slate-400 whitespace-nowrap">{formatTimeAgo(activity.created_at)}</span>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name === 'revenue' ? 'Gelir' : p.name === 'orders' ? 'Siparis' : 'Kullanici'}: {
            p.name === 'revenue' ? `${p.value.toLocaleString('tr-TR')} TL` : p.value
          }
        </p>
      ))}
    </div>
  );
};

const AdminOverviewTab = ({ stats, formatPrice }) => {
  const [chartData, setChartData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [actLoading, setActLoading] = useState(true);

  useEffect(() => {
    const fetchChart = async () => {
      try {
        const res = await admin.dashboardChart(30);
        const formatted = (res.data || []).map(d => ({
          ...d,
          date: d.date?.slice(5),
        }));
        setChartData(formatted);
      } catch { /* silently fail */ }
      finally { setChartLoading(false); }
    };

    const fetchActivities = async () => {
      try {
        const res = await admin.getActivityLogs?.({ limit: 15 }) 
          || (await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/activity-logs?limit=15`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })).json());
        const data = res.data || res;
        setActivities(data.logs || []);
      } catch { /* silently fail */ }
      finally { setActLoading(false); }
    };

    fetchChart();
    fetchActivities();
  }, []);

  const metrics = [
    {
      title: 'Toplam Gelir',
      value: formatPrice ? formatPrice(stats?.totalRevenue || 0) : `${(stats?.totalRevenue || 0).toLocaleString('tr-TR')} TL`,
      icon: DollarSign, color: 'green', subtitle: 'Odenmis siparisler'
    },
    {
      title: 'Toplam Siparis',
      value: (stats?.totalOrders || 0).toLocaleString('tr-TR'),
      icon: ShoppingCart, color: 'blue', subtitle: 'Tum siparisler'
    },
    {
      title: 'Toplam Urun',
      value: (stats?.totalProducts || 0).toLocaleString('tr-TR'),
      icon: Package, color: 'purple', subtitle: 'Yayinda'
    },
    {
      title: 'Toplam Kullanici',
      value: (stats?.totalUsers || 0).toLocaleString('tr-TR'),
      icon: Users, color: 'amber', subtitle: 'Kayitli'
    },
    {
      title: 'Aktif Satici',
      value: (stats?.totalSellers || 0).toLocaleString('tr-TR'),
      icon: Store, color: 'red', subtitle: 'Onayli saticilar'
    },
    {
      title: 'Bekleyen Onay',
      value: (stats?.pendingSellers || 0).toLocaleString('tr-TR'),
      change: stats?.pendingSellers > 5 ? 'Acil' : null,
      changeType: stats?.pendingSellers > 5 ? 'negative' : null,
      icon: Clock, color: 'slate', subtitle: 'Satici basvurusu'
    }
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Hos Geldiniz, Admin</h1>
          <p className="text-slate-500 mt-1">Iste bugunku platform durumunuz</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Clock className="w-4 h-4" />
          Son guncelleme: {new Date().toLocaleTimeString('tr-TR')}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {metrics.map((metric, index) => (
          <div key={index}><MetricCard {...metric} /></div>
        ))}
      </div>

      {/* Chart & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend Chart */}
        <Card className="lg:col-span-2 border-slate-200/50" data-testid="sales-trend-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Satis Trendi</CardTitle>
              <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 font-medium">Son 30 Gun</span>
            </div>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="orders" name="orders" stroke="#3b82f6" fill="url(#colorOrders)" strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="revenue" name="revenue" stroke="#10b981" fill="url(#colorRevenue)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-sm text-slate-400">
                Henuz siparis verisi yok
              </div>
            )}
            {!chartLoading && chartData.length > 0 && (
              <div className="flex items-center gap-4 mt-2 justify-center">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <div className="w-3 h-1 rounded bg-blue-500" /> Siparis
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <div className="w-3 h-1 rounded bg-emerald-500" /> Gelir (TL)
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-slate-200/50" data-testid="recent-activities">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Son Aktiviteler</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {actLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : activities.length > 0 ? (
              <div className="divide-y divide-slate-100 max-h-[340px] overflow-y-auto">
                {activities.map((activity, index) => (
                  <RecentActivityItem key={activity.id || index} activity={activity} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-slate-400">Henuz aktivite yok</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverviewTab;
